import { useQuery } from '@tanstack/react-query';
import { ChangeEvent, FC, useMemo, useRef, useState } from 'react';
import { Control, useController } from 'react-hook-form';
import styled from 'styled-components';

import { Input, Label } from '@/components/Forms';
import ValidationMessage from '@/components/Forms/ValidationMessage';
import {
  ElevenLabsVoice,
  MLProjectTranslationVoiceoverSettings,
} from '@/types/resources';
import { api } from '@/utils/api';
import { scrollBarStyles } from '@/utils/theme';

import VoiceOption from './VoiceOption';

const Wrap = styled.div`
  position: relative;
`;

const Options = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  width: 100%;
  max-height: 400px;
  overflow-y: auto;
  padding: 4px;
  box-shadow: 0px 4px 8px 0px #25282b1f;
  display: flex;
  flex-direction: column;
  gap: 4px;
  background: ${({ theme }) => theme.colors.white};
  z-index: 2;
  ${scrollBarStyles};
`;

const InputWrap = styled.div`
  position: relative;
  > svg {
    position: absolute;
    right: 15px;
    top: 0;
    bottom: 0;
    margin: auto 0;
  }
`;

const StyledInput = styled(Input)`
  cursor: pointer;
`;

interface Props {
  control: Control<{
    voiceoverSettings: MLProjectTranslationVoiceoverSettings;
  }>;
}

const VoiceField: FC<Props> = ({ control }) => {
  const [search, setSearch] = useState('');
  const [isInputFocused, setIsInputFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const { data: voices } = useQuery(
    ['eleven_labs_voices'],
    () =>
      api.get<never, ElevenLabsVoice[]>('/v1/distribution/eleven-labs-voices'),
    {
      staleTime: Infinity,
    },
  );

  const {
    fieldState: { error },
    field: { value, onChange },
  } = useController<{
    voiceoverSettings: MLProjectTranslationVoiceoverSettings;
  }>({
    control,
    name: 'voiceoverSettings.voiceId',
  });

  const activeOption = useMemo(() => {
    return voices?.find((v) => v.id === value);
  }, [voices, value]);

  const onVoiceChange = (voice: ElevenLabsVoice) => {
    onChange(voice.id);
    setSearch(voice.name);
    inputRef.current?.blur();
  };

  const onInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  const filteredVoices = voices?.filter((v) =>
    v.name.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <Wrap>
      <Label>Voice</Label>
      <InputWrap>
        <StyledInput
          ref={inputRef}
          placeholder="Select voice"
          onFocus={() => setIsInputFocused(true)}
          onBlur={() => setIsInputFocused(false)}
          onChange={onInputChange}
          value={isInputFocused ? search : activeOption?.name ?? ''}
        />
        <svg
          width="8"
          height="6"
          viewBox="0 0 8 6"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M4 5.5L8 0.5H0L4 5.5Z" fill="currentColor" />
        </svg>
      </InputWrap>
      {isInputFocused && (
        <Options onMouseDown={(e) => e.preventDefault()}>
          {filteredVoices?.map((voice) => (
            <VoiceOption
              key={voice.id}
              voice={voice}
              onChange={onVoiceChange}
            />
          ))}
        </Options>
      )}
      {!!error && <ValidationMessage>{error.message}</ValidationMessage>}
    </Wrap>
  );
};

export default VoiceField;
